import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { showingbookerAPI } from '../../../services/showingbookerAPI';
import StripePayment from './StripePayment';
import CalendarIntegration from '../../../components/CalendarIntegration';
import PropertyHeaderComponent from '../../../components/PropertyHeader';
import { getAvailableTimeSlots, formatTime } from '../../../utils/availabilityChecker';

// Generate available dates (next 14 days, starting 24 hours from now) - TIMEZONE SAFE
const getAvailableDates = () => {
  const dates = [];
  const today = new Date();
  const minimumDate = new Date(today.getTime() + (24 * 60 * 60 * 1000)); // 24 hours from now
  
  for (let i = 0; i <= 14; i++) {
    const date = new Date(minimumDate);
    date.setDate(minimumDate.getDate() + i);
    
    // TIMEZONE FIX: Use local date components to avoid UTC conversion issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    
    dates.push({
      value: dateString,
      label: date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        month: 'short', 
        day: 'numeric' 
      })
    });
  }
  return dates;
};

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 16px;
  
  @media (min-width: 768px) {
    padding: 24px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #1a1a1a;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 16px;
  color: #1a1a1a;
  
  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 1px #2563eb;
  }
`;

const RequesterTypeGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 24px;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const RequesterTypeButton = styled.button`
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: ${props => props.selected ? '#2563eb' : 'white'};
  color: ${props => props.selected ? 'white' : '#1a1a1a'};
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  min-height: 56px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: ${props => props.selected ? '#1d4ed8' : '#f8fafc'};
    border-color: #2563eb;
    transform: translateY(-1px);
  }
  
  @media (min-width: 768px) {
    min-height: 64px;
  }
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 24px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 2px;
    background: #e2e8f0;
    z-index: 0;
  }
`;

const StepDot = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.active ? '#2563eb' : props.completed ? '#10b981' : '#e2e8f0'};
  color: ${props => props.active || props.completed ? 'white' : '#64748b'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  position: relative;
  z-index: 1;
`;

const BookingForm = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  padding: 24px;
  margin-bottom: 24px;
`;

const FormTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 16px 0;
`;

const FormSection = styled.div`
  margin-bottom: 24px;
`;

const PriceTag = styled.h3`
  font-size: 20px;
  font-weight: 500;
  color: #2563eb;
  margin: 8px 0;
`;

const NoticeText = styled.p`
  color: #64748b;
  font-size: 14px;
  margin: 8px 0;
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 16px;
  color: #1a1a1a;
  margin-top: 8px;
  
  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 1px #2563eb;
  }
`;

const TimeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 16px;
  margin-top: 16px;
`;

const TimeButton = styled.button`
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  color: #1a1a1a;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
  min-height: 56px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #2563eb;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #f1f5f9;
    color: #94a3b8;
    cursor: not-allowed;
  }
  
  &.selected {
    background: #2563eb;
    color: white;
    border-color: #2563eb;
    box-shadow: 0 2px 4px rgba(37,99,235,0.1);
  }
  
  @media (min-width: 768px) {
    min-height: 64px;
  }
`;

const TermsCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 24px 0;
  
  input {
    width: 20px;
    height: 20px;
  }
  
  label {
    font-size: 14px;
    color: #64748b;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 14px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background: #1d4ed8;
  }
  
  &:disabled {
    background: #94a3b8;
    cursor: not-allowed;
  }
`;

const BookingPage = ({ embedded, propertySlug }) => {
  const { propertyId } = useParams();
  const [property, setProperty] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [existingBookings, setExistingBookings] = useState([]);
  const [bookingData, setBookingData] = useState(null);
  const [agentProfile, setAgentProfile] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);

  const [formData, setFormData] = useState({
    showingDate: '',
    showingTime: '',
    requesterType: '',
    name: '',
    email: '',
    phone: '',
    specialRequests: '',
    agreeToTerms: false
  });
  
  useEffect(() => {
    const loadProperty = async () => {
      const identifier = propertySlug || propertyId;
      console.log('Loading property:', identifier);
      
      try {
        // Use the API service instead of raw fetch
        const data = await showingbookerAPI.getPublicProperty(identifier);
        console.log('Property data:', data);
        
        // Check if property is unlisted
        if (data.status === 'unlisted') {
          // Redirect to property unavailable page
          window.location.href = `/property-unavailable/${identifier}`;
          return;
        }
        
        setProperty(data);
        
        // Load availability data including existing bookings
        try {
          const availabilityData = await showingbookerAPI.getPublicPropertyAvailability(identifier);
          console.log('📅 Loaded availability data:', availabilityData);
          
          // Update property with availability info
          setProperty(prev => ({
            ...prev,
            custom_availability: availabilityData.availability.custom_availability,
            availability_type: availabilityData.availability.availability_type,
            buffer_time_minutes: availabilityData.availability.buffer_time_minutes
          }));
          
          // Set existing bookings for availability checking
          setExistingBookings(availabilityData.existing_bookings || []);
          console.log('📅 Loaded existing bookings:', availabilityData.existing_bookings);
        } catch (availabilityError) {
          console.error('⚠️ Could not load availability:', availabilityError.message);
          setExistingBookings([]);
        }
        
      } catch (error) {
        console.error('❌ Property not found');
        setError(`Property not found. This property may have been removed or the booking link may be invalid. Property ID/Slug: ${identifier}`);
      } finally {
        setLoading(false);
      }
    };

    loadProperty();
  }, [propertyId, propertySlug]);

  // Load time slots with availability checking
  const loadTimeSlots = async (selectedDate) => {
    if (!property || !selectedDate || !property.id) {
      console.log('🔧 loadTimeSlots: Early return - missing data:', {
        property: !!property,
        selectedDate,
        propertyId: property?.id
      });
      setTimeSlots([]);
      return;
    }
    
    try {
      // Use the availability checker utility to get slots with availability status
      const availableSlots = await getAvailableTimeSlots(property, selectedDate, existingBookings);
      
      // Transform the slots to include formatted labels and availability status
      const formattedSlots = availableSlots.map(slot => ({
        value: slot.time,
        label: formatTime(slot.time),
        available: slot.available,
        reason: slot.reason
      }));
      
      setTimeSlots(formattedSlots);
    } catch (error) {
      console.error('🔧 Error loading time slots:', error);
      setTimeSlots([]);
    }
  };

  // Load time slots when date changes
  useEffect(() => {
    if (formData.showingDate && property?.id) {
      loadTimeSlots(formData.showingDate);
    }
  }, [formData.showingDate, property, existingBookings]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'showingDate' && value !== formData.showingDate) {
      setFormData({
        ...formData,
        [name]: value,
        showingTime: ''
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  const handleRequesterTypeSelect = (type) => {
    setFormData({
      ...formData,
      requesterType: type,
      // Reset other fields when changing type
      name: '',
      email: '',
      phone: '',
      showingDate: '',
      showingTime: ''
    });
  };

  const handleTimeSlotSelect = (time) => {
    setFormData({
      ...formData,
      showingTime: time
    });
  };

  const calculateTotal = () => {
    return 10.00; // Flat rate pricing
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    
    // Show payment form
    const bookingDataForPayment = {
      ...formData,
      propertyId: property?.id
    };
    setShowPayment(true);
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  const handlePaymentSuccess = (paymentResult) => {
    setPaymentComplete(true);
    setBookingData(paymentResult.booking);
    setShowPayment(false);
  };

  const handlePaymentError = (error) => {
    setError(error);
    setShowPayment(false);
  };

  const moveToNextStep = () => {
    setCurrentStep(prev => Math.min(prev + 1, 3));
  };

  const moveToPreviousStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  if (loading) {
    return <div>Loading property details...</div>;
  }

  if (error && !property) {
    return <div>Error loading property: {error}</div>;
  }

  if (!property) {
    return <div>No property data available</div>;
  }

  return (
    <Container>
      <PropertyHeaderComponent property={property} />

      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '16px', 
        flexWrap: 'wrap',
        margin: '24px 0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="20" height="20">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span style={{ fontSize: '14px', color: '#64748b' }}>24-hour notice required</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="20" height="20">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <span style={{ fontSize: '14px', color: '#64748b' }}>Secure online payment</span>
        </div>
      </div>

      <StepIndicator>
        <StepDot completed={currentStep > 1} active={currentStep === 1}>1</StepDot>
        <StepDot completed={currentStep > 2} active={currentStep === 2}>2</StepDot>
        <StepDot active={currentStep === 3}>3</StepDot>
      </StepIndicator>

      <BookingForm>
        <FormTitle>
          {currentStep === 1 ? 'Select Showing Date & Time' : 
           currentStep === 2 ? 'Enter Your Contact Details' : 
           'Complete Payment'}
        </FormTitle>

        <form onSubmit={handleSubmit}>
          {currentStep === 1 && (
            <FormSection>
              <Label>Select a Date</Label>
              <Select 
                name="showingDate" 
                value={formData.showingDate} 
                onChange={handleChange} 
                required
                style={{ marginBottom: '24px' }}
              >
                <option value="">Choose your preferred date</option>
                {getAvailableDates().map(date => (
                  <option key={date.value} value={date.value}>{date.label}</option>
                ))}
              </Select>

              {formData.showingDate && (
                <>
                  <Label>Select a Time</Label>
                  {timeSlots.length > 0 ? (
                    <TimeGrid>
                      {timeSlots.map(slot => (
                        <TimeButton
                          key={slot.value}
                          type="button"
                          onClick={() => handleTimeSlotSelect(slot.value)}
                          disabled={!slot.available}
                          className={formData.showingTime === slot.value ? 'selected' : ''}
                        >
                          {slot.label}
                        </TimeButton>
                      ))}
                    </TimeGrid>
                  ) : (
                    <div style={{
                      padding: '20px',
                      textAlign: 'center',
                      color: '#64748b',
                      background: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      margin: '16px 0'
                    }}>
                      <p style={{ margin: 0, fontSize: '16px' }}>
                        No showing times available for this day.
                      </p>
                      <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
                        Please select another date to check for availability.
                      </p>
                    </div>
                  )}
                </>
              )}

              <SubmitButton 
                type="button" 
                onClick={moveToNextStep}
                disabled={!formData.showingDate || !formData.showingTime}
                style={{ marginTop: '24px' }}
              >
                Continue to Contact Details
              </SubmitButton>
            </FormSection>
          )}

          {currentStep === 2 && (
            <FormSection>
              <RequesterTypeGroup>
                <RequesterTypeButton 
                  type="button"
                  selected={formData.requesterType === 'buyers_agent'}
                  onClick={() => handleRequesterTypeSelect('buyers_agent')}
                >
                  Buyer's Agent
                </RequesterTypeButton>
                <RequesterTypeButton 
                  type="button"
                  selected={formData.requesterType === 'buyer'}
                  onClick={() => handleRequesterTypeSelect('buyer')}
                >
                  Buyer
                </RequesterTypeButton>
                <RequesterTypeButton 
                  type="button"
                  selected={formData.requesterType === 'renter'}
                  onClick={() => handleRequesterTypeSelect('renter')}
                >
                  Prospective Renter
                </RequesterTypeButton>
              </RequesterTypeGroup>

              {formData.requesterType === 'buyers_agent' && (
                <>
                  <FormGroup>
                    <Label>Name *</Label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter your name"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="Enter your email"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Phone *</Label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="Enter your phone number"
                    />
                  </FormGroup>
                </>
              )}

              {(formData.requesterType === 'buyer' || formData.requesterType === 'renter') && (
                <>
                  <FormGroup>
                    <Label>Name *</Label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter your name"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="Enter your email"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Phone *</Label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="Enter your phone number"
                    />
                  </FormGroup>
                </>
              )}

              <TermsCheckbox>
                <input
                  type="checkbox"
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleChange}
                  required
                />
                <label>I agree to the Terms of Service</label>
              </TermsCheckbox>

              <SubmitButton 
                type="button" 
                onClick={moveToNextStep}
                disabled={!formData.requesterType || !formData.name || !formData.email || !formData.phone || !formData.agreeToTerms}
              >
                Continue to Payment
              </SubmitButton>
            </FormSection>
          )}

          {currentStep === 3 && !paymentComplete && (
            <FormSection>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <PriceTag>${calculateTotal()} flat rate platform fee</PriceTag>
                <NoticeText>Secure payment powered by Stripe</NoticeText>
              </div>
              <SubmitButton 
                type="submit" 
                disabled={!formData.agreeToTerms || showPayment}
              >
                Pay ${calculateTotal()} and Book Showing
              </SubmitButton>
            </FormSection>
          )}

          {currentStep === 3 && paymentComplete && (
            <FormSection>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <h3 style={{ color: '#10b981', marginBottom: '8px' }}>✅ Booking Confirmed!</h3>
                <p style={{ color: '#64748b', fontSize: '14px' }}>
                  Confirmation #{bookingData?.confirmation_number}
                </p>
              </div>
            </FormSection>
          )}
        </form>

        {showPayment && !paymentComplete && (
          <StripePayment
            amount={calculateTotal()}
            bookingData={{
              ...formData,
              propertyId: property?.id
            }}
            propertyData={property}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentError={handlePaymentError}
          />
        )}

        {paymentComplete && bookingData && (
          <CalendarIntegration 
            booking={bookingData}
            property={property}
            compact={false}
          />
        )}
      </BookingForm>
    </Container>
  );
};

export default BookingPage;